import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../../services/api";
import MediaUploader from "./MediaUploader";
import '../CrmModals.css';


const LeadMessageModal = ({ channel: initialChannel = "WHATSAPP", lead, onSend, onClose }) => {
  const [formData, setFormData] = useState({
    channel: initialChannel,
    content: "",
    subject: "",
    toEmail: "",
    integration_id: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // Estado para mídia
  const [mediaInfo, setMediaInfo] = useState(null);

  // Ref para o textarea - evita document.getElementById que conflita com React
  const textareaRef = useRef(null);

  // Estado para integrações
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrationsError, setIntegrationsError] = useState(null);

  // Buscar integrações ao montar o modal
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setIntegrationsLoading(true);
        setIntegrationsError(null);
        const response = await api.get("/api/integrations");
        const allIntegrations = response.data || [];
        setIntegrations(allIntegrations);
      } catch (err) {
        console.error("Erro ao carregar integrações:", err);
        setIntegrationsError("Não foi possível carregar integrações");
      } finally {
        setIntegrationsLoading(false);
      }
    };

    fetchIntegrations();
  }, []);

  // Filtrar integrações baseado no canal selecionado
  const getFilteredIntegrations = () => {
    if (formData.channel === "WHATSAPP") {
      return integrations.filter(
        (i) => i.type === "whatsapp" || i.key === "whatsapp_evolution"
      );
    }
    if (formData.channel === "EMAIL") {
      return integrations.filter(
        (i) => i.type === "email" || i.key === "email"
      );
    }
    return [];
  };

  const filteredIntegrations = getFilteredIntegrations();

  // Auto-selecionar se houver apenas uma integração
  useEffect(() => {
    if (filteredIntegrations.length === 1 && !formData.integration_id) {
      setFormData((prev) => ({
        ...prev,
        integration_id: filteredIntegrations[0].id,
      }));
    } else if (filteredIntegrations.length === 0) {
      setFormData((prev) => ({
        ...prev,
        integration_id: "",
      }));
    }
  }, [filteredIntegrations.length, formData.channel]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      channel: initialChannel || "WHATSAPP",
      toEmail: lead?.email || prev.toEmail || "",
    }));
  }, [initialChannel, lead]);

  const validateEmailFormat = (email) => {
    if (!email) return false;
    return /\S+@\S+\.\S+/.test(email);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.channel) newErrors.channel = "Canal obrigatório";

    // Permitir mensagem vazia se houver mídia (apenas para WhatsApp)
    if (!formData.content.trim() && !mediaInfo) {
      newErrors.content = "Mensagem obrigatória";
    }

    // Validar integração para WhatsApp e Email
    if (formData.channel === "WHATSAPP" || formData.channel === "EMAIL") {
      if (filteredIntegrations.length > 0 && !formData.integration_id) {
        newErrors.integration = "Selecione uma integração";
      }
    }

    if (formData.channel === "EMAIL") {
      if (!formData.subject.trim()) {
        newErrors.subject = "Assunto obrigatório para email";
      }
      if (!formData.toEmail.trim()) {
        newErrors.toEmail = "Destinatário obrigatório";
      } else if (!validateEmailFormat(formData.toEmail.trim())) {
        newErrors.toEmail = "Email inválido";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers para mídia
  const handleMediaUpload = (media) => {
    setMediaInfo(media);
  };

  const handleMediaRemove = () => {
    setMediaInfo(null);
  };

  // Função para inserir variável usando useRef (evita document.getElementById)
  const insertVariable = useCallback((variable) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setFormData((prev) => ({ ...prev, content: (prev.content || "") + variable }));
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content || "";
    const newText = text.substring(0, start) + variable + text.substring(end);

    setFormData((prev) => ({ ...prev, content: newText }));

    // Usar requestAnimationFrame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    });
  }, [formData.content]);

  const getPreviewContent = () => {
    const safeLead = lead || {};
    const replacements = {
      "{{lead.name}}": safeLead.name || "John Doe",
      "{{lead.email}}": safeLead.email || "john@example.com",
      "{{lead.phone}}": safeLead.phone || "+5511999999999",
      "{{lead.company}}": safeLead.company || "Acme Corp",
    };

    let preview = formData.content || "";
    Object.entries(replacements).forEach(([token, value]) => {
      preview = preview.replace(new RegExp(token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"), value);
    });
    return preview;
  };

  const handleSend = async () => {
    if (!validate()) return;

    try {
      setSending(true);

      if (onSend) {
        await onSend({
          channel: formData.channel,
          content: formData.content,
          subject: formData.subject,
          toEmail: formData.toEmail,
          lead,
          integration_id: formData.integration_id ? Number(formData.integration_id) : null,
          media_id: mediaInfo?.id || null,
        });
      }

      setSending(false);
      onClose();
    } catch (err) {
      console.error(err);
      setSending(false);
      setErrors((prev) => ({
        ...prev,
        global: "Erro ao enviar mensagem. Tente novamente.",
      }));
    }
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h3 className="crm-modal-title">Enviar mensagem</h3>
            <p className="crm-modal-subtitle">Selecione o canal e escreva sua mensagem</p>
          </div>
          <button onClick={onClose} className="crm-modal-close">
            &times;
          </button>
        </div>

        <div className="crm-modal-body">
          {lead && (
            <div className="crm-info-card crm-mb-5">
              <div className="crm-info-card-label">
                Para: {lead.name} {!!lead.company && `· ${lead.company} `}
              </div>
              <div className="crm-text-muted-sm">
                {!!lead.email && <div>Email: {lead.email}</div>}
                {!!lead.phone && <div>Telefone: {lead.phone}</div>}
              </div>
            </div>
          )}

          <div className="crm-form-field crm-mb-5">
            <label className="crm-form-label">Canal *</label>
            <select
              value={formData.channel}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  channel: e.target.value,
                  integration_id: "",
                }))
              }
              className={`crm-select ${errors.channel ? "crm-input-error" : ""} `}
            >
              <option value="WHATSAPP">WhatsApp</option>
              <option value="EMAIL">Email</option>
              <option value="PHONE_CALL">Ligação (registro)</option>
            </select>
            {errors.channel && <span className="crm-error-message">{errors.channel}</span>}
          </div>

          {(formData.channel === "WHATSAPP" || formData.channel === "EMAIL") && (
            <div className="crm-form-field crm-mb-5">
              <label className="crm-form-label">Integração *</label>
              {integrationsLoading ? (
                <div className="crm-text-muted-sm">Carregando integrações...</div>
              ) : integrationsError ? (
                <div className="crm-error-message">{integrationsError}</div>
              ) : filteredIntegrations.length === 0 ? (
                <div className="crm-error-message">
                  Nenhuma integração de {formData.channel === "WHATSAPP" ? "WhatsApp" : "Email"} configurada
                </div>
              ) : (
                <>
                  <select
                    value={formData.integration_id}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        integration_id: e.target.value,
                      }))
                    }
                    className={`crm-select ${errors.integration ? "crm-input-error" : ""}`}
                  >
                    {filteredIntegrations.length > 1 && (
                      <option value="">Selecione uma integração</option>
                    )}
                    {filteredIntegrations.map((integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.label || integration.name}
                      </option>
                    ))}
                  </select>
                  {errors.integration && (
                    <span className="crm-error-message">{errors.integration}</span>
                  )}
                </>
              )}
            </div>
          )}

          {formData.channel === "EMAIL" && (
            <>
              <div className="crm-form-field crm-mb-5">
                <label className="crm-form-label">Para *</label>
                <input
                  type="email"
                  value={formData.toEmail}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      toEmail: e.target.value,
                    }))
                  }
                  placeholder="email@exemplo.com"
                  className={`crm-input ${errors.toEmail ? "crm-input-error" : ""} `}
                />
                {errors.toEmail && <span className="crm-error-message">{errors.toEmail}</span>}
              </div>

              <div className="crm-form-field crm-mb-5">
                <label className="crm-form-label">Assunto *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  placeholder="Assunto do email"
                  className={`crm-input ${errors.subject ? "crm-input-error" : ""} `}
                />
                {errors.subject && <span className="crm-error-message">{errors.subject}</span>}
              </div>
            </>
          )}

          {/* Media Uploader - apenas para WhatsApp */}
          {formData.channel === "WHATSAPP" && (
            <div className="crm-form-field crm-mb-5">
              <label className="crm-form-label">Anexar mídia (opcional)</label>
              <MediaUploader
                onUpload={handleMediaUpload}
                onRemove={handleMediaRemove}
                mediaInfo={mediaInfo}
                disabled={sending}
              />
            </div>
          )}

          <div className="crm-form-field crm-mb-5">
            <label className="crm-form-label">
              Mensagem {mediaInfo ? "(opcional)" : "*"}
            </label>
            <div className="crm-template-variables">
              <span className="crm-template-label">Inserir variáveis:</span>
              <button type="button" className="crm-tag" onClick={() => insertVariable("{{lead.name}}")}>
                Nome
              </button>
              <button type="button" className="crm-tag" onClick={() => insertVariable("{{lead.email}}")}>
                Email
              </button>
              <button type="button" className="crm-tag" onClick={() => insertVariable("{{lead.phone}}")}>
                Telefone
              </button>
              <button type="button" className="crm-tag" onClick={() => insertVariable("{{lead.company}}")}>
                Empresa
              </button>
            </div>
            <textarea
              ref={textareaRef}
              id="lead-message-content"
              value={formData.content}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
              className={`crm-textarea ${errors.content ? "crm-input-error" : ""} `}
              placeholder={mediaInfo ? "Legenda da mídia (opcional)" : "Digite sua mensagem aqui. Use variáveis como {{lead.name}}."}
              rows="5"
            />
            {errors.content && <span className="crm-error-message">{errors.content}</span>}
          </div>

          {formData.content && (
            <div className="crm-message-preview">
              <strong className="crm-text-strong-block">Pré-visualização:</strong>
              <div className="crm-preview-content">{getPreviewContent()}</div>
            </div>
          )}

          {errors.global && <div className="crm-error-message crm-mt-3">{errors.global}</div>}
        </div>

        <div className="crm-modal-footer">
          <button onClick={onClose} className="crm-btn crm-btn-secondary">
            Cancelar
          </button>
          <button onClick={handleSend} className="crm-btn crm-btn-primary" disabled={sending}>
            {sending ? "Enviando..." : "Enviar mensagem"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadMessageModal;
